import { capitalize } from '@mui/material';
import { t } from '@ankr.com/common';
import {
  EBlockchainType,
  Chain,
  ChainID,
  ChainType,
  TAC_TESTNETS,
} from '@ankr.com/chains-list';

import { getChainGroups, getName } from 'modules/endpoints/constants/groups';
import { Tab } from 'modules/common/hooks/useTabs';
import { getUniqueArray } from 'modules/common/utils/getUniqueArray';

import { flatChain } from './flatChain';
import { isMultichain } from './isMultichain';
import { renderTacName } from './renderTacNames';

// tron has two mainnets
const IGNORE_MAINNET_FILTERING = [ChainID.TRON];

const HIDDEN_SUBCHAIN_LABELS = [
  ChainID.HORIZEN_EVM,
  ChainID.HORIZEN_TESTNET_EVM,
  ChainID.TENET_EVM,
  ChainID.ZERO_G_GALILEO_BEACONKIT_REST,
];

const EXCLUDED_TESTNETS = [ChainID.ETH];

export const getChainLabels = (
  chain: Chain,
  chainTypes: Tab<ChainType>[],
): string[] => {
  if (isMultichain(chain.id)) {
    // multichain should have no labels
    return [];
  }

  const isTac = chain.id === ChainID.TAC;
  const isPolkadot = chain.id === ChainID.POLKADOT;
  const isZeroG = chain.id === ChainID.ZERO_G;

  const flattedChain = flatChain(chain);

  // filter out empty paths and mainnet groups
  const subchains = flattedChain.filter(subchain => {
    // tron has two mainnets
    if (!IGNORE_MAINNET_FILTERING.includes(subchain.id)) {
      // exclude mainnet group
      return subchain.id !== chain.id;
    }

    return subchain.paths && subchain.paths.length > 0;
  });

  const isOnlyOneTestnet = chain.testnets?.length === 1;
  const isOnlyOneDevnet = chain.devnets?.length === 1;
  const isOnlyOneSignet = chain.signets?.length === 1;

  const EXCLUDED_GROUPS = [
    getName('standard-evm-api'),
    getName('ethereum-endpoint'),
  ];

  // collect subchain labels
  const labels = subchains.map(subchain => {
    if (isTac) {
      return TAC_TESTNETS.includes(subchain.id)
        ? renderTacName(subchain.id)
        : '';
    }

    const shouldIgnoreTestnetGroup =
      isOnlyOneTestnet && subchain.type === EBlockchainType.Testnet;
    const shouldIgnoreDevnetGroup =
      isOnlyOneDevnet && subchain.type === EBlockchainType.Devnet;
    const shouldIgnoreSignetGroup =
      isOnlyOneSignet && subchain.type === EBlockchainType.Signet;
    const shouldIgnoreLabel =
      shouldIgnoreTestnetGroup ||
      shouldIgnoreDevnetGroup ||
      shouldIgnoreSignetGroup ||
      HIDDEN_SUBCHAIN_LABELS.includes(subchain.id);

    if (shouldIgnoreLabel) {
      return '';
    }

    const currentGroup = getChainGroups().find(group => {
      return group.chains.find(c => c === subchain.id);
    });

    if (currentGroup) {
      const { name } = currentGroup;

      if (EXCLUDED_GROUPS.includes(name)) {
        return '';
      }

      return name;
    }

    return '';
  });

  if (isPolkadot) {
    labels.push(t('chain-item.header.polkadot-label'));
  }

  if (isTac || isPolkadot) {
    labels.sort((a, b) => a.length - b.length);
  }

  // add mainnet/testnet/devnet to start of labels
  labels.unshift(...chainTypes.map(type => capitalize(type.id)));

  // exclude testnet label
  if (EXCLUDED_TESTNETS.includes(chain.id)) {
    labels.splice(labels.indexOf(capitalize(ChainType.Testnet)), 1);
  }

  // add beacon label if chain has beaconkit subchain
  if (subchains.find(subchain => subchain.id.includes('beaconkit-rest'))) {
    labels.push(t('chain-item.header.beaconkit-rest-label'));
  }

  // add beacon label if chain has beacon subchain
  if (subchains.find(subchain => subchain.id.includes('beacon')) && !isZeroG) {
    labels.push(t('chain-item.header.beacon-label'));
  }

  // add opnode label if chain has opnode subchain
  if (subchains.find(subchain => subchain.id.includes('opnode'))) {
    labels.push(t('chain-item.header.opnode-label'));
  }

  // filter out empty labels
  labels.filter(Boolean);

  // remove duplicates
  return getUniqueArray(labels);
};
